<template>
  <div class="detail-card" data-card-id="moderation">
    <div
      class="card-header"
      :class="{ 'slidein-header': mode === 'draggable' }"
    >
      <div class="header-left">
        <h5 class="card-title">
          AI Moderation / Feedback <span class="subtitle">(per image)</span>
        </h5>
      </div>
      <div class="header-right">
        <button
          class="drag-toggle-btn"
          @click.prevent="$emit('toggleDraggable')"
          :title="mode === 'draggable' ? 'Dock to panel' : 'Make draggable'"
        >
          <font-awesome-icon
            :icon="mode === 'draggable' ? ['fas', 'times'] : ['fas', 'clone']"
          />
        </button>
      </div>
    </div>
    <div class="card-content">
      <div class="detail-item" v-if="latestModeration">
        <span class="detail-label">Created on:</span>
        <span class="detail-value">{{
          formatDateTime(latestModeration.created_at, "yyyy-MM-dd HH:mm")
        }}</span>
      </div>
      <div class="detail-item" v-if="latestModeration">
        <span class="detail-label">Moderated by:</span>
        <span class="detail-value">{{
          latestModeration.created_by?.personal?.fullname || "N/A"
        }}</span>
      </div>
      <div class="detail-item">
        <span class="detail-label">AI / Moderation Variability:</span>
        <span class="detail-value">
          <strong class="text-white"
            >{{ ((1 - aiFeedbackAccuracy) * 100).toFixed(2) }}%</strong
          >
        </span>
      </div>
      <div class="expandable-section">
        <a
          href="#"
          @click.prevent="moderationOpen = !moderationOpen"
          class="section-toggle"
        >
          <font-awesome-icon
            :icon="
              moderationOpen
                ? ['fas', 'chevron-down']
                : ['fas', 'chevron-right']
            "
            class="me-2"
          />
          <strong class="text-white">Details</strong>
        </a>
        <div v-if="moderationOpen" class="pathology-list">
          <div
            v-for="pathology in aiPathologies.filter(
              (p) =>
                p.ai.predicted_diagnosis !== 'Thoracic' &&
                p.ai.predicted_diagnosis !== 'Cardio',
            )"
            :key="pathology.ai._id"
            class="pathology-item"
          >
            <div class="pathology-icon">
              <font-awesome-icon
                :icon="
                  pathology.feedback
                    ? ['fas', 'check-circle']
                    : ['fas', 'times-circle']
                "
                :class="
                  pathology.feedback ? 'status-confirmed' : 'status-rejected'
                "
              />
            </div>
            <div class="pathology-name">
              {{ pathology.ai.predicted_diagnosis }}
              <small
                v-if="pathology.ai.location && pathology.ai.location.length > 0"
              >
                ({{ pathology.ai.location.join(", ") }})
              </small>
            </div>
          </div>
        </div>
        <div
          v-if="moderationOpen && latestModeration.other?.length > 0"
          class="analysis-details mt-0"
        >
          <h6 class="mt-2 text-white">Other pathologies</h6>
          <div class="pathology-list">
            <div
              v-for="pathology in latestModeration.other"
              :key="'other_' + pathology"
              class="pathology-item"
            >
              <div class="pathology-icon">
                <font-awesome-icon
                  :icon="['fas', 'times-circle']"
                  class="status-rejected"
                />
              </div>
              <div class="pathology-name">
                {{ pathology }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { formatDateTime } from "@/helpers/project-management/formatters";

const props = defineProps({
  latestModeration: Object,
  aiFeedbackAccuracy: Number,
  aiPathologies: Array,
  mode: {
    type: String,
    default: "parked",
  },
});

defineEmits(["toggleDraggable"]);

const moderationOpen = ref(false);
</script>

<style scoped>
/* Shared styles (migrated from global styles.css) */
.analysis-details {
  margin-top: 0.5rem;
}

.status-confirmed {
  color: #28a745;
}

.status-partial {
  color: var(--color-accent-primary);
}

.status-rejected {
  color: var(--color-error);
}

.pathology-list {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0rem;
}

.pathology-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background-color: var(--color-bg-primary);
  border-radius: var(--border-radius-small);
}

.pathology-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.pathology-name {
  flex: 1;
  color: var(--color-text-primary);
  font-size: 0.9rem;
}

.pathology-name small {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
}
</style>
